// Maps ApiClientError codes to user-facing messages.
// Centralized so every screen surfaces the same copy for the same
// error condition. Keep messages second-person, plain, no jargon.
//
// Add new codes here as the backend grows; the default falls back to
// a friendly generic.

import { ApiClientError } from './api';

const MESSAGES: Record<string, string> = {
  // Network / offline
  queued_offline:
    "Saved offline — we'll sync next time you open the app within 7 days.",
  network_error: "We can't reach the server. Check your connection.",
  too_old: 'That entry is more than 7 days old. Log it from today instead.',

  // Auth. session_revoked = users.session_epoch was bumped after this
  // cookie was issued (logout-all on another device). idle_too_long =
  // the JWT's `lat` claim is older than the 2h hard idle limit. Both
  // need a re-login; messaging matches `unauthenticated` so the user
  // sees the same friendly nudge regardless of which mechanism fired.
  unauthenticated: 'Your session expired. Sign in again.',
  revoked: 'Your session was signed out. Sign in again.',
  session_revoked: 'Your session expired. Sign in again.',
  idle_too_long: 'Your session expired. Sign in again.',
  invalid_credentials: 'Email or password not recognized.',
  csrf: 'Security check failed. Refresh the page and try again.',
  rate_limited: 'Too many attempts. Wait a few minutes and try again.',

  // Validation
  invalid_input: "That doesn't look right — check the fields and try again.",
  invalid_json: 'Something went wrong sending that. Try again.',

  // Registration / profile
  email_in_use: 'That email is already registered. Sign in instead.',
  same_password: 'New password must be different from the current one.',
  same_email: 'That is already your email.',
  display_name_taken: 'That display name is taken — try another.',
  display_name_blocked: 'That name isn’t allowed — try a different one.',
  display_name_required: 'Pick a display name first.',
  too_soon: "You changed it recently. Try again in 24 hours.",

  // Streak
  no_freezes: "You're out of streak freezes for this month.",
  recently_used: 'You already used a freeze recently.',

  // Baseline
  no_baseline: "You don't have a baseline yet — set up your taper plan first.",
  cooldown: "You did this recently. Try again later.",

  // Server
  internal_error: 'Something went wrong on our end. Try again in a moment.',
  http_error: 'Something went wrong. Try again.',
};

export function apiErrorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ApiClientError) {
    // queued_offline is a "success-ish" outcome — never blame the user.
    if (err.code in MESSAGES) return MESSAGES[err.code];
    // Network failures throw native Error (TypeError); ApiClientError is server-shaped.
    return fallback ?? `Something went wrong (${err.code}). Try again.`;
  }
  if (err instanceof TypeError) {
    return MESSAGES.network_error;
  }
  return fallback ?? 'Something went wrong. Try again.';
}

// Convenience: was the failure an "already handled" non-error (e.g. queued)?
export function isQueuedOffline(err: unknown): boolean {
  return err instanceof ApiClientError && err.code === 'queued_offline';
}
